import { useState, useCallback } from "react";
import {
  MoveHorizontal,
  MoveVertical,
  Maximize2,
  PanelTop,
  PanelBottom,
  PanelLeft,
  PanelRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { parseNumber } from "@/lib/visual-editor/helper";
import PropertySection from "../PropertySection";

interface SpacingSectionProps {
  styles: Record<string, string>;
  onStyleChange: (property: string, value: string) => void;
  onSpacingChange: (
    type: "margin" | "padding",
    side: string,
    value: string | number,
  ) => void;
  onBatchSpacingChange: (
    type: "margin" | "padding",
    sides: Record<string, string | number>,
  ) => void;
}

/** Compact spacing input with icon inside */
function SpacingInput({
  icon: Icon,
  value,
  onChange,
}: {
  icon: LucideIcon;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}): React.JSX.Element {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === "") {
        onChange(undefined);
        return;
      }
      const num = parseInt(raw, 10);
      if (!isNaN(num)) {
        onChange(num);
      }
    },
    [onChange],
  );

  return (
    <div className="flex items-center gap-1.5 rounded-md border border-input bg-background px-2 h-8 min-w-0">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <input
        type="text"
        inputMode="numeric"
        value={value ?? ""}
        onChange={handleChange}
        placeholder="0"
        className="w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
      />
    </div>
  );
}

function SpacingGroup({
  label,
  type,
  styles,
  onSpacingChange,
  onBatchSpacingChange,
}: {
  label: string;
  type: "margin" | "padding";
  styles: Record<string, string>;
  onSpacingChange: (
    type: "margin" | "padding",
    side: string,
    value: string | number,
  ) => void;
  onBatchSpacingChange: (
    type: "margin" | "padding",
    sides: Record<string, string | number>,
  ) => void;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const handleLinkedChange = useCallback(
    (axis: "horizontal" | "vertical", value: number | undefined) => {
      const sides =
        axis === "horizontal" ? ["Left", "Right"] : ["Top", "Bottom"];
      const v = value ?? "";
      onBatchSpacingChange(type, { [sides[0]]: v, [sides[1]]: v });
    },
    [type, onBatchSpacingChange],
  );

  const handleSideChange = useCallback(
    (side: string, value: number | undefined) => {
      onSpacingChange(type, side, value ?? "");
    },
    [type, onSpacingChange],
  );

  // For linked mode, derive horizontal/vertical values from Left/Top
  const horizontalValue =
    parseNumber(styles[`${type}Left`]) ?? parseNumber(styles[`${type}Right`]);
  const verticalValue =
    parseNumber(styles[`${type}Top`]) ?? parseNumber(styles[`${type}Bottom`]);

  return (
    <div className="flex-1 min-w-0">
      <span className="text-xs text-muted-foreground font-medium mb-1.5 block">
        {label}
      </span>

      {!expanded ? (
        /* Linked mode: horizontal + vertical inputs + toggle */
        <div className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0">
            <SpacingInput
              icon={MoveHorizontal}
              value={horizontalValue}
              onChange={(v) => handleLinkedChange("horizontal", v)}
            />
          </div>
          <div className="flex-1 min-w-0">
            <SpacingInput
              icon={MoveVertical}
              value={verticalValue}
              onChange={(v) => handleLinkedChange("vertical", v)}
            />
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
            title="Edit individual sides"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        /* Expanded mode: 4 individual side inputs + toggle */
        <div className="flex items-start gap-1.5">
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-1.5">
            <SpacingInput
              icon={PanelTop}
              value={parseNumber(styles[`${type}Top`])}
              onChange={(v) => handleSideChange("Top", v)}
            />
            <SpacingInput
              icon={PanelRight}
              value={parseNumber(styles[`${type}Right`])}
              onChange={(v) => handleSideChange("Right", v)}
            />
            <SpacingInput
              icon={PanelBottom}
              value={parseNumber(styles[`${type}Bottom`])}
              onChange={(v) => handleSideChange("Bottom", v)}
            />
            <SpacingInput
              icon={PanelLeft}
              value={parseNumber(styles[`${type}Left`])}
              onChange={(v) => handleSideChange("Left", v)}
            />
          </div>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className={cn(
              "shrink-0 h-8 w-8 flex items-center justify-center rounded-md border transition-colors cursor-pointer",
              "border-primary bg-primary text-primary-foreground hover:bg-primary/90",
            )}
            title="Link sides"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export function SpacingSection({
  styles,
  onSpacingChange,
  onBatchSpacingChange,
}: SpacingSectionProps): React.JSX.Element {
  return (
    <PropertySection title="Spacing" defaultOpen>
      <div className="flex gap-4">
        <SpacingGroup
          label="Margin"
          type="margin"
          styles={styles}
          onSpacingChange={onSpacingChange}
          onBatchSpacingChange={onBatchSpacingChange}
        />
        <SpacingGroup
          label="Padding"
          type="padding"
          styles={styles}
          onSpacingChange={onSpacingChange}
          onBatchSpacingChange={onBatchSpacingChange}
        />
      </div>
    </PropertySection>
  );
}
